import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable } from 'mobx';
import { Link } from '../../../../../Resource/Types';
import ModalStore from '../../../../Modal/Store';
import LearningCollectionsStore from '../../../Store';
import { LearningCollectionSummaryResource } from '../../../Types';
import {
  LearningCollectionError,
  LearningCollectionState,
  error,
  hide,
  loading,
  publishing,
  ready,
  waiting,
} from './Types';

class LearningCollectionStore {
  @observable
  state: LearningCollectionState = waiting();
  readonly learningCollectionsStore: LearningCollectionsStore;

  constructor(learningCollectionsStore: LearningCollectionsStore) {
    makeObservable(this);
    this.learningCollectionsStore = learningCollectionsStore;
  }

  @action
  loading = (learningCollectionSummaryResource: LearningCollectionSummaryResource) => {
    switch (this.state.kind) {
      case 'ready':
      case 'loading':
      case 'error':
      case 'publishing':
      case 'hide':
        break;
      case 'waiting':
        this.state = loading(learningCollectionSummaryResource);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = (learningCollectionSummaryResource: LearningCollectionSummaryResource) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'error':
        break;
      case 'hide':
      case 'publishing':
      case 'loading':
        this.state = ready(learningCollectionSummaryResource);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateResource = (learningCollectionSummaryResource: LearningCollectionSummaryResource) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = ready(learningCollectionSummaryResource);
        break;
      case 'waiting':
      case 'error':
      case 'loading':
      case 'publishing':
      case 'hide':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  publishing = () => {
    switch (this.state.kind) {
      case 'ready':
        this.state = publishing(this.state);
        break;
      case 'waiting':
      case 'error':
      case 'loading':
      case 'publishing':
      case 'hide':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  hide = (hideModalStore: ModalStore) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = hide(this.state, hideModalStore);
        break;
      case 'waiting':
      case 'error':
      case 'loading':
      case 'publishing':
      case 'hide':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (err: LearningCollectionError) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
        break;
      case 'ready':
      case 'publishing':
      case 'hide':
        this.state = error(err, this.state);
        break;
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return [];
      case 'ready':
      case 'publishing':
      case 'hide':
        return this.state.learningCollectionSummaryResource.links;
    }
  }
}

export default LearningCollectionStore;
